import { parseToRgb } from 'polished';

import { GOOGLE_MAPS_KEY } from '@/constants/global';
import { isDefined } from '@/utils/helpers';
import {
  Country,
  getArrayOfLength,
  isNotEmpty,
  nullOrNumber,
  PropertyEnum
} from '@proprioo/hokkaido';

import {
  ListingFeesRecurrenceType,
  ListingFrontType,
  ListingHeatingLocation,
  ListingHeatingMode,
  ListingHeatingType,
  ListingLivingRoomViewType,
  ListingParkingType,
  ListingPictureProps,
  ListingQuality,
  ListingWindowType
} from '../interfaces';
import { isFrenchCountryListing } from '../selling/ListingSelling.utils';
import { getPictureUrl } from '../selling/mediaPanel/MediaPanel.utils';
import {
  THUMBNAIL_MAX_WIDTH,
  THUMBNAIL_RATIO
} from '../selling/mediaPanel/picture/Picture.styles';
import { GenerateInformationProps } from './information/tableInformation/TableInformation';
import { generatePlaceholderPicture } from './placeholder';
import { housePropertyTypes } from '../utils';

export const computeArrayOfPictures = (
  nbPlaceholders: number,
  othersPictures: ListingPictureProps[]
): ListingPictureProps[] => [
  ...othersPictures,
  ...getArrayOfLength(nbPlaceholders).map(generatePlaceholderPicture)
];

export const computePictureFromAssets = ({
  attachment,
  dir,
  name
}: ListingPictureProps): string =>
  dir && name
    ? getPictureUrl(
        { dir, name },
        {
          height: THUMBNAIL_MAX_WIDTH * THUMBNAIL_RATIO,
          width: THUMBNAIL_MAX_WIDTH
        }
      )
    : attachment;

export const formatFacade = (facade?: ListingFrontType): string => {
  switch (facade) {
    case ListingFrontType.CUT_STONE:
      return 'stonebrick';
    case ListingFrontType.BRICK:
      return 'brick';
    case ListingFrontType.CONCRETE:
      return 'concrete';
    case ListingFrontType.COAT:
      return 'coat';
    case ListingFrontType.CLASSIC:
      return 'classic';
    case ListingFrontType.NOT_MENTIONED:
      return 'dontKnow';
    case ListingFrontType.STAPLED_STONE:
      return 'stapledStone';
    case ListingFrontType.GRINDING_STONE:
      return 'stoneMillstone';
    default:
      return 'other';
  }
};

export const formatFeesRecurrence = (
  type?: ListingFeesRecurrenceType
): string => {
  switch (type) {
    case ListingFeesRecurrenceType.MONTHLY:
      return 'month';
    case ListingFeesRecurrenceType.QUARTERLY:
      return 'quarter';
    case ListingFeesRecurrenceType.ANNUAL:
      return 'year';
    default:
      return 'month';
  }
};

export const formatFloor = (
  type: PropertyEnum,
  floor?: nullOrNumber
): string => {
  const isGroundFloor = !isDefined(floor) || floor === 0;

  if (isGroundFloor && isHouse(type)) return 'singleStorey';
  return isGroundFloor ? 'groundFloor' : '';
};

export const formatHeating = (heating: ListingHeatingMode | string): string => {
  if (
    Object.values(ListingHeatingMode).includes(heating as ListingHeatingMode)
  ) {
    switch (heating) {
      case ListingHeatingMode.CPCU:
        return 'cpcu';
      case ListingHeatingMode.ELECTRIC:
        return 'electric';
      case ListingHeatingMode.FUEL:
        return 'fuel';
      case ListingHeatingMode.GAS:
        return 'gas';
      case ListingHeatingMode.GEOTHERMAL:
        return 'geothermal';
      case ListingHeatingMode.HEAT_PUMP:
        return 'heatpump';
      case ListingHeatingMode.PELLETS:
        return 'pellets';
      case ListingHeatingMode.SOLAR:
        return 'solar';
      case ListingHeatingMode.WOOD:
        return 'wood';
      default:
        return 'unknown';
    }
  }

  return heating;
};

export const formatHeatingLocation = (
  location?: ListingHeatingLocation
): string => {
  switch (location) {
    case ListingHeatingLocation.UNDERFLOOR:
      return 'underfloor';
    case ListingHeatingLocation.WALL:
      return 'wall';
    default:
      return 'other';
  }
};

export const formatHeatingType = (
  type?: ListingHeatingType | number
): string =>
  type === ListingHeatingType.COMMUNAL || type === 1
    ? 'collectiveHeating'
    : 'individual';

export const formatLivingRoomViewType = (
  livingRoomView?: ListingLivingRoomViewType
): string => {
  switch (livingRoomView) {
    case ListingLivingRoomViewType.STREET:
      return 'street';
    case ListingLivingRoomViewType.COURTYARD:
      return 'courtyard';
    case ListingLivingRoomViewType.SEA:
      return 'sea';
    case ListingLivingRoomViewType.PARK:
      return 'park';
    case ListingLivingRoomViewType.GARDEN:
      return 'garden';
    default:
      return 'dontKnow';
  }
};

export const formatLuminosity = (luminosityScore: number): string => {
  if (luminosityScore) return luminosityScore < 5 ? 'bright' : 'veryBright';
  return 'dontKnow';
};

export const formatParkingType = (type?: ListingParkingType): string => {
  switch (type) {
    case ListingParkingType.INSIDE:
      return 'inside';
    case ListingParkingType.OUTSIDE:
      return 'outside';
    default:
      return 'other';
  }
};

export const formatQuality = (
  quality?: ListingQuality,
  country = Country.FRANCE
): string => {
  const isFrenchListing = isFrenchCountryListing(country);

  switch (quality) {
    case ListingQuality.CORRECT:
      return isFrenchListing ? 'goodState' : 'habitable';
    case ListingQuality.NEED:
      return isFrenchListing ? 'toRenovate' : 'needsRenovation';
    case ListingQuality.NEW:
      return isFrenchListing ? 'newOrRefurbished' : 'new';
    case ListingQuality.REFRESH:
      return 'toRefresh';
    case ListingQuality.REFURBISHED:
      return 'refurbished';
    case ListingQuality.UNDER_CONSTRUCTION:
      return 'underConstruction';
    default:
      return 'other';
  }
};

export const formatWindowType = (windowType?: ListingWindowType): string => {
  switch (windowType) {
    case ListingWindowType.SINGLE_GLAZING:
      return 'simpleWindow';
    case ListingWindowType.DOUBLE_GLAZING:
      return 'doubleWindow';
    case ListingWindowType.TRIPLE_GLAZING:
      return 'tripleWindow';
    default:
      return 'dontKnow';
  }
};

export const formatYesOrNo = (value?: boolean): string =>
  value ? 'yes' : 'no';

export const generateGoogleAPIMap = (
  latitude: number,
  longitude: number
): string =>
  `https://maps.googleapis.com/maps/api/staticmap?center=${latitude},${longitude}&zoom=17&size=640x520&markers=color:0x00D49C|${latitude},${longitude}&format=jpg&key=${GOOGLE_MAPS_KEY}`;

export const generateInformation = (
  informations: GenerateInformationProps[]
): GenerateInformationProps[][] => {
  const array = [];

  const filteredInformations = informations.filter(
    ({ isVisible }) => isVisible
  );

  for (let i = 0; i < filteredInformations.length; i = i + 2) {
    const current = filteredInformations[i];
    const next =
      filteredInformations.length > i + 1 && filteredInformations[i + 1];

    if (next) {
      array.push([current, next]);
    } else {
      array.push([current, { isVisible: true, label: '', text: '' }]);
    }
  }

  return array;
};

export const isCoproVotedBuildNotEmpty = (coproVotedBuild?: string): boolean =>
  isNotEmpty(coproVotedBuild) &&
  coproVotedBuild !== '0' &&
  coproVotedBuild !== 'Aucun';

export const isHouse = (type: PropertyEnum): boolean =>
  housePropertyTypes.includes(type);

export const hexToRgb = (str: string) => {
  const hexTest = /#[a-f\d]{3,6}/gim;

  return str.replace(hexTest, hexColor => {
    const { red, green, blue } = parseToRgb(hexColor);

    return `rgb(${red}, ${green}, ${blue})`;
  });
};
